import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { captureException } from '@sentry/node';

import { doRequest } from '@r1-frontend/do-request/index';
import { getQrCode } from '@r1-frontend/api-domru/api-pay/v1/qr/index';
import { getQrLink } from '@r1-frontend/api-domru/api-pay/v1/vtb-pay/qr-core';
import { IVtbPayQrCode } from '@r1-frontend/api-domru/api-pay/v1/vtb-pay/qr-core/dto/IVtbPayQrCode';

import { btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';

import { selectCardPaySum } from '~/src/features/payments-abn/components/SeasonTicketPayment/store/selectors/selectPaymentSum';
import { pushDataLayer } from '~/src/features/payments-abn/helpers/pushDataLayer';
import { setPaymentQrCode } from '~/src/features/payments-abn/store/actionCreators';
import { TState } from '~/src/store';
import { openChat } from '~/src/store/actions/chat';

import SbpInstruction from './SbpInstruction';

import * as ST from './styled';

interface IProps {
    providerId: number,
    userAgreement?: string,
    isDisabled: boolean,
    isLoadingQrData: boolean,
    setIsLoadingQrData: Dispatch<SetStateAction<boolean>>,
    onPayHandler: (reqAction: () => Promise<void>) => void,
}

const DesktopSbp = ({
    providerId,
    userAgreement = '',
    isDisabled,
    isLoadingQrData,
    onPayHandler,
    setIsLoadingQrData,
}: IProps): JSX.Element => {
    const dispatch = useDispatch();
    const { sbpData: { qrCode } } = useSelector((state: TState) => state.payment);
    const cardPaymentSum = useSelector(selectCardPaySum);

    const [isQrCodeError, setIsQrCodeError] = useState(false);

    const fetchQrCode = useCallback(async() => {
        pushDataLayer('click on show qrcode', 'click qrcode');
        setIsLoadingQrData(true);

        try {
            const linkResponse = await doRequest.apiPay<IVtbPayQrCode>(
                getQrLink({ providerId, userAgreement, paySum: cardPaymentSum }),
            );

            if (!linkResponse.isSuccess) {
                throw new Error(linkResponse.error.message);
            }

            const _code = await doRequest.apiPay<string>(
                getQrCode(providerId, linkResponse.payload.data.payload),
            );

            if (_code.isSuccess && _code?.payload) {
                dispatch(setPaymentQrCode(_code.payload));
                pushDataLayer('click on show qrcode', 'succes qrcode');
            } else {
                setIsQrCodeError(true);
                pushDataLayer('click on show qrcode', 'error qrcode');
            }

        } catch (error) {
            captureException(error);
        } finally {
            setIsLoadingQrData(false);
        }
    }, [setIsLoadingQrData, providerId, userAgreement, cardPaymentSum, dispatch]);

    return (
        <ST.QrBlock>
            <ST.QrSide>{qrCode
                ? <ST.QrInner dangerouslySetInnerHTML={{ __html: qrCode }}/>
                : <ST.QrInner>
                    {isQrCodeError && (
                        <ST.ErrorMessage>
                            Ошибка. Попробуйте снова или обратитесь в&nbsp;<ST.SupportLink onClick={() => dispatch(openChat())}>поддержку</ST.SupportLink>.
                        </ST.ErrorMessage>
                    )}
                    <ST.QrButton
                        type={btnTypes.SECONDARY}
                        size={btnSizes.SMALL}
                        text={'Показать QR-код'}
                        disabled={isDisabled}
                        isLoading={isLoadingQrData}
                        onClick={() => onPayHandler(fetchQrCode)}
                    />
                </ST.QrInner>
            }</ST.QrSide>
            <ST.InfoSide>
                <ST.InfoTitle>Как оплатить через СБП</ST.InfoTitle>
                <SbpInstruction/>
            </ST.InfoSide>
        </ST.QrBlock>
    );
};

export default DesktopSbp;
